import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { buildWaiter } from '@ember/test-waiters';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format, isValid, parseISO } from 'date-fns';
import { didCancel, dropTask, enqueueTask, restartableTask, task, timeout } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { runTask } from 'ember-lifeline';
import { modifier } from 'ember-modifier';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, eq, not, or } from 'ember-truth-helpers';
import DirectUploader from 'garaje/components/direct-uploader';
import EnvoyOneUpCalendar from 'garaje/components/envoy-one-up-calendar';
import UiCard from 'garaje/components/ui/card';
import type { DocumentStatus } from 'garaje/components/visitors/document-status-badge';
import DocumentStatusBadge from 'garaje/components/visitors/document-status-badge';
import DocumentPreviewModal from 'garaje/components/visitors/profiles/document-preview-modal';
import type { ProfileDocument } from 'garaje/components/visitors/profiles/document-status';
import type CustomUserDocumentTemplateModel from 'garaje/models/custom-user-document-template';
import type DraftDocumentModel from 'garaje/models/draft-document';
import type { DraftDocumentFieldValues, DraftDocumentStatus } from 'garaje/models/draft-document';
import type UserDocumentTemplateConfigurationModel from 'garaje/models/user-document-template-configuration';
import type VisitorDocumentModel from 'garaje/models/visitor-document';
import {
  confirmDraftDocument,
  pollDraftDocumentWithDeadline,
  requestUploadLink,
  uploadFile,
} from 'garaje/utils/draft-documents';
import type { ValidationResult } from 'utils/direct-uploader';

// Re-exported so the list (and tests) share one canonical shape (A2's model).
export type { DraftDocumentFieldValues, DraftDocumentStatus };

// Client-side dropzone constraints for admin pre-reg draft documents.
// 10 MB client cap (the backend allows 100 MB) — accept PDF / JPG / PNG.
const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';
const ACCEPTED_MIME_REGEX = /^(application\/pdf|image\/jpeg|image\/png)$/;
const MAX_FILE_SIZE_BYTES = 10_000_000;

// The upload mixes ED/jQuery calls (tracked by `settled()`) with a raw-XHR S3 PUT and an
// ember-concurrency `timeout()` poll — neither of which `settled()` observes (EC registers no
// test-waiter). Without an explicit waiter, `await triggerEvent('change')`/`settled()` can return
// mid-upload, before `@onDraftChange` reports the draft id to the spine, so the invite save races
// ahead of the collect. This waiter makes `settled()` block until the whole task (incl. the final
// `onDraftChange`) completes. Compiles to a no-op in production.
const uploadWaiter = buildWaiter('document-review-card:uploadDraftTask');

type ExpirationEntry = { name: string; date: Date | null };
/** A row with both a name and a parsed date — the output of `parseEntries`. */
type CompleteExpirationEntry = { name: string; date: Date };

/**
 * Per-card create-mode lifecycle phase. Loading/parsing/error UI derives from THIS
 * (the task lifecycle), not the draft model's raw `status` — a failed or timed-out
 * parse must leave a recoverable state, not a perpetual spinner.
 */
type UploadPhase = 'idle' | 'uploading' | 'parsing' | 'completed' | 'error';

export type DocumentBusyKind = 'upload' | 'field-values-save';

interface Signature {
  Args: {
    document?: VisitorDocumentModel;
    template?: CustomUserDocumentTemplateModel;
    configuration?: UserDocumentTemplateConfigurationModel;
    isUploaded?: boolean;
    showUndo?: boolean;
    onStatusChange?: () => void;
    /** 'review' (default) = approve/deny an uploaded doc. 'create' = admin pre-reg upload + parse. */
    mode?: 'review' | 'create';
    /** Create mode — required for the upload link. */
    locationId?: string | null;
    /**
     * Create mode — reports this card's draft id up to the spine so the invite-create can
     * reference it via the write-only `draft-documents` relationship. Fires as soon as the
     * draft exists (parsing never blocks submit), and with `null` on remove/replace OR when
     * an upload/parse error leaves no usable draft.
     */
    onDraftChange?: (templateId: string, draftId: string | null) => void;
    /**
     * Create mode — reports whether THIS card has an upload in flight: `true` while the file
     * is uploading (the pre-confirm window where no draft id exists yet), `false` once the
     * draft is created (parsing onward), on error, and on remove. The spine aggregates these
     * to disable Submit while any document is still uploading — otherwise the invite could be
     * created before the draft id is collected, silently dropping the document. Parsing does
     * NOT count as in-flight (the id is already reported, and the design allows submit then).
     *
     * `kind` distinguishes the two busy classes so the spine can gate them differently:
     * 'upload' (seconds-long, hard-blocks submit) vs 'field-values-save' (a ~200ms PATCH the
     * spine's saveTask waits out, so the click that committed an edit can also submit).
     */
    onUploadStateChange?: (templateId: string, isUploading: boolean, kind: DocumentBusyKind) => void;
    /**
     * Optional controlled override (used by isolated component tests): when provided these
     * take precedence over the self-driven draft so rows/parsing can be rendered without
     * standing up the transport.
     */
    status?: DraftDocumentStatus;
    fieldValues?: DraftDocumentFieldValues;
    /** Test seam: shrink the parse-poll interval/timeout so loop/timeout paths run fast. */
    pollOptions?: { intervalMs?: number; timeoutMs?: number };
  };
  Element: HTMLDivElement;
}

export default class DocumentReviewCard extends Component<Signature> {
  @service declare store: StoreService;

  @tracked expirationEntries: ExpirationEntry[] = [];
  /** Tracks which calendar popover is open: a numeric index, or null */
  @tracked openCalendar: number | null = null;
  // True while keyboard focus is inside the expiration rows — a half-filled row is the
  // NORMAL mid-entry state (name committed, date not yet picked), so the incomplete-row
  // error must not interrupt until the user actually leaves the rows.
  @tracked isEditingExpirationRows = false;
  private expirationFieldsElement: HTMLElement | null = null;

  // ----- create-mode state -----
  @tracked draft: DraftDocumentModel | null = null;
  @tracked selectedFile: File | null = null;
  @tracked previewDataUrl: string | null = null;
  // Blob URL for the in-memory PDF preview iframe. Chrome refuses to render a
  // base64 `data:` PDF in an iframe, so a `blob:` object URL is used instead;
  // it must be revoked on reset/teardown to avoid leaking the object.
  @tracked previewObjectUrl: string | null = null;
  @tracked uploadPhase: UploadPhase = 'idle';
  @tracked uploadErrorMessage: string | null = null;
  @tracked saveErrorMessage: string | null = null;
  // Last upload-in-flight state reported to the spine (dedupes change notifications).
  private busyReported: Record<DocumentBusyKind, boolean> = { upload: false, 'field-values-save': false };

  // Exposed for the dropzone template.
  readonly acceptedFileTypes = ACCEPTED_FILE_TYPES;
  readonly acceptedMimeRegex = ACCEPTED_MIME_REGEX;
  readonly maxFileSizeBytes = MAX_FILE_SIZE_BYTES;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.initExpirationEntries();
  }

  get isCreateMode(): boolean {
    return this.args.mode === 'create';
  }

  // Per-template test hook for the create form (the `[data-test-admin-prereg-document="<id>"]` seam).
  get adminPreregTemplateId(): string | undefined {
    return this.isCreateMode ? this.args.template?.id : undefined;
  }

  // ----- create-mode display state -----
  // Live path is driven by `uploadPhase`; isolated tests drive it via `@status` (no live task).
  get createCompleted(): boolean {
    return this.isCreateMode && (this.args.status === 'completed' || this.uploadPhase === 'completed');
  }

  get createInProgress(): boolean {
    if (!this.isCreateMode) return false;
    return (
      this.args.status === 'processing' ||
      this.args.status === 'pending' ||
      this.uploadPhase === 'uploading' ||
      this.uploadPhase === 'parsing'
    );
  }

  // Recoverable failure (upload error or parse timeout). Controlled tests have no error state.
  get createErrored(): boolean {
    return this.isCreateMode && this.uploadPhase === 'error';
  }

  // Unique input id per required-document card so multiple dropzones don't collide on
  // DirectUploader's default id="file-upload".
  get uploadInputId(): string {
    return `draft-upload-${this.args.template?.id ?? 'new'}`;
  }

  // Accessible name for the (visually-hidden) file input, keyed off the document title so
  // each required-document dropzone is distinctly named for screen-reader / keyboard users.
  get uploadInputAriaLabel(): string {
    const name = this.args.template?.title ?? 'document';
    return `Upload ${name} (PDF, JPG, or PNG, 10 MB max)`;
  }

  // A completed parse that surfaced no dates (vs. dates the admin simply hasn't entered yet),
  // so the lone blank row isn't mistaken for a bug. Keyed off the seeded rows — the exact thing
  // the hint explains — so it matches whatever `parseEntries` produced (an empty map AND a map of
  // unparseable values both collapse to the single auto-inserted blank row).
  get parseReturnedNoDates(): boolean {
    if (!this.createCompleted) return false;
    const [only] = this.expirationEntries;
    return this.expirationEntries.length === 1 && !only?.name && !only?.date;
  }

  private parseDate(dateStr: string): Date | null {
    // Extract date portion to avoid UTC off-by-one for ISO timestamps
    // e.g. "2026-10-15T00:00:00.000Z" should parse as Oct 15, not Oct 14 in PST
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
    if (dateMatch) {
      const d = new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]));
      return isNaN(d.getTime()) ? null : d;
    }
    const parsed = parseISO(dateStr);
    return isValid(parsed) ? parsed : null;
  }

  // Parse a free-form `{ label -> date-string }` map into complete (name + date) rows.
  private parseEntries(data: Record<string, unknown>): CompleteExpirationEntry[] {
    return Object.entries(data)
      .filter(([, v]) => typeof v === 'string')
      .map(([name, dateStr]) => ({ name, date: this.parseDate(String(dateStr)) }))
      .filter((e): e is CompleteExpirationEntry => e.date !== null);
  }

  private initExpirationEntries(): void {
    const parsed = this.parseEntries(this.args.document?.inputFieldsData ?? {});
    this.expirationEntries = parsed.length === 0 && this.requiresExpirationDate ? [{ name: '', date: null }] : parsed;
  }

  // Seed editable rows from a completed parse. On create we always surface at least one row
  // so the admin can hand-enter dates when the parse found nothing (there is no `failed`
  // state — empty is a valid outcome). `fieldValues` is frozen on the model; we only read it.
  private seedExpirationEntriesFromMap(data: Record<string, unknown>): void {
    const parsed = this.parseEntries(data);
    this.expirationEntries = parsed.length > 0 ? parsed : [{ name: '', date: null }];
  }

  // Controlled-override seeding ONLY: isolated tests pass @status/@fieldValues with no draft.
  // The live path seeds in `uploadDraftTask` instead — our own `updateFieldValues` PATCH
  // reassigns `draft.fieldValues`, so a modifier keyed on it would re-seed and clobber an
  // in-progress (added/edited) row on every auto-save. Mode-gated so a stray @status in
  // review mode can't overwrite rows seeded from the uploaded doc's `inputFieldsData`.
  seedFromControlledArgs = modifier(
    (
      _element: Element,
      [status, fieldValues]: [DraftDocumentStatus | undefined, DraftDocumentFieldValues | undefined],
    ) => {
      if (!this.isCreateMode || status !== 'completed') return;
      this.seedExpirationEntriesFromMap(fieldValues ?? {});
    },
  );

  get title(): string | undefined {
    return this.args.isUploaded ? this.args.document?.title : this.args.template?.title;
  }

  get status(): DocumentStatus {
    if (!this.args.isUploaded) return 'not-uploaded';
    const doc = this.args.document;
    if (doc?.isApproved) return 'approved';
    if (doc?.isDenied) return 'denied';
    return 'review';
  }

  // Review mode always shows the approval badge. Create mode shows "NOT UPLOADED" only at
  // rest (nothing selected/uploading/done) — the post-convert APPROVED badge belongs to the
  // review/detail path, not the create form.
  get showStatusBadge(): boolean {
    // The create form never shows a status badge (Figma): the empty state is just the
    // dropzone, and the post-convert APPROVED badge belongs to the review/detail path.
    if (this.isCreateMode) return false;
    return true;
  }

  get uploadDate(): string | null {
    const attachment = this.args.document?.mostRecentAttachment;
    const date = attachment?.updatedAt;
    if (!date) {
      const docDate = this.args.document?.updatedAt;
      return docDate ? format(docDate, 'MMM d, yyyy, h:mm a') : null;
    }
    return format(date, 'MMM d, yyyy, h:mm a');
  }

  get externalAttachmentUrl(): string | null {
    return this.args.document?.externalAttachments?.[0]?.url ?? null;
  }

  @tracked imageFailedToLoad = false;

  get isPdf(): boolean {
    return this.imageFailedToLoad;
  }

  // True only when we have a renderable in-memory image preview. Falls back to the filename
  // tile for PDFs AND for images whose FileReader preview never produced a data URL.
  get hasImagePreview(): boolean {
    return Boolean(this.selectedFile?.type.startsWith('image/')) && this.previewDataUrl !== null;
  }

  // In-memory PDF preview source: blob URL + the same viewer-chrome-hiding hash
  // the review-mode iframe uses, so the dropped file renders as a clipped first
  // page rather than a generic file tile.
  get createPreviewPdfUrl(): string | null {
    return this.previewObjectUrl ? `${this.previewObjectUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH` : null;
  }

  // The document is "discarded" once the backend has anonymized the
  // attachment, signalled by an `anonymized_at` timestamp on the first
  // external attachment. When set, the preview area renders the "Document
  // discarded" placeholder instead of the regular preview/PDF.
  get isDiscarded(): boolean {
    // Top-level anonymizedAt is set for both Veriff and profile docs. Fall back to
    // the per-attachment flag; redacted Veriff docs have an empty externalAttachments
    // array, so the per-attachment check alone would miss them.
    return !!(this.args.document?.anonymizedAt ?? this.args.document?.externalAttachments?.[0]?.anonymized_at);
  }

  get mediaOverlayClass(): string {
    return 'absolute inset-0 flex items-center justify-center gap-2 bg-black/0 opacity-0 group-hover:bg-black/40 group-hover:opacity-100 transition-all duration-200 pointer-events-none';
  }

  @action handleImageError(): void {
    this.imageFailedToLoad = true;
  }

  get pdfViewerUrl(): string {
    return `${this.externalAttachmentUrl}#navpanes=0&toolbar=0`;
  }

  get documentFileName(): string {
    return this.title ?? 'document.pdf';
  }

  @tracked showPreviewModal = false;

  get previewDocuments(): ProfileDocument[] {
    return [this.previewDocument];
  }

  get previewDocument(): ProfileDocument {
    return {
      id: this.args.document?.id ?? '',
      title: this.title ?? '',
      downloadUrl: this.externalAttachmentUrl ?? undefined,
      approvalStatus: this.args.document?.isApproved ? 'approved' : this.args.document?.isDenied ? 'denied' : 'review',
      createdAt: this.args.document?.createdAt?.toISOString(),
      fieldValues: this.args.document?.inputFieldsData,
    };
  }

  @action openDocument(): void {
    this.showPreviewModal = true;
  }

  @action closePreviewModal(): void {
    this.showPreviewModal = false;
  }

  get documentLink(): VisitorDocumentModel['userDocumentLink'] {
    return this.args.document?.userDocumentLink;
  }

  get isPending(): boolean {
    return this.status === 'review';
  }

  get requiresExpirationDate(): boolean {
    return this.args.configuration?.reUploadConfig?.policy === 'on_expiration';
  }

  // Editable rows render for an uploaded doc (review) or a completed draft (create).
  get showExpirationRows(): boolean {
    if (this.isCreateMode) return this.createCompleted;
    return Boolean(this.args.isUploaded);
  }

  get parsingLabel(): string {
    return this.uploadPhase === 'uploading' ? 'Uploading…' : 'Parsing document…';
  }

  get hasAnyCompleteDate(): boolean {
    return this.expirationEntries.some((e) => e.name && e.date);
  }

  get approveDisabled(): boolean {
    if (this.approveTask.isRunning) return true;
    if (this.requiresExpirationDate && !this.hasAnyCompleteDate) return true;
    if (this.hasIncompleteExpirationEntries) return true;
    return false;
  }

  formatDate = (date: Date | null): string => (date ? format(date, 'MMM d, yyyy') : 'Select date');

  @action toggleCalendar(id: number): void {
    if (this.openCalendar === id) {
      this.closeCalendar();
    } else {
      this.openCalendar = id;
      requestAnimationFrame(() => {
        if (this.isDestroying || this.isDestroyed) return;
        document.addEventListener('click', this.handleOutsideClick, true);
      });
    }
  }

  captureExpirationFields = modifier((element: Element) => {
    this.expirationFieldsElement = element as HTMLElement;
    const onFocusIn = (): void => {
      this.isEditingExpirationRows = true;
    };
    const onFocusOut = (event: Event): void => {
      if (element.contains((event as FocusEvent).relatedTarget as Node)) return;
      this.isEditingExpirationRows = false;
    };
    element.addEventListener('focusin', onFocusIn);
    element.addEventListener('focusout', onFocusOut);
    return () => {
      element.removeEventListener('focusin', onFocusIn);
      element.removeEventListener('focusout', onFocusOut);
    };
  });

  // Defer the incomplete-row error until focus leaves the rows: announcing "both name and
  // date are required" while the admin is between filling the name and picking the date
  // reads as a premature failure (reported by QA as "error pops up early and blocks you").
  // `approveDisabled` / the save-skip still gate on the raw incompleteness immediately.
  get showIncompleteRowError(): boolean {
    return this.hasIncompleteExpirationEntries && !this.isEditingExpirationRows;
  }

  handleOutsideClick = (event: MouseEvent): void => {
    if (this.expirationFieldsElement?.contains(event.target as Node)) return;
    this.closeCalendar();
  };

  closeCalendar = (): void => {
    this.openCalendar = null;
    document.removeEventListener('click', this.handleOutsideClick, true);
  };

  willDestroy(): void {
    super.willDestroy();
    document.removeEventListener('click', this.handleOutsideClick, true);
    // An upload can be in flight while the card is destroyed (e.g. started during the
    // flow-switch async window, before the new flow's templates land). Retract the
    // in-flight report so the spine's Submit gate isn't left disabled forever.
    this.reportUploadInFlight(false);
    this.reportUploadInFlight(false, 'field-values-save');
    this.#setPreviewObjectUrl(null);
  }

  // Revoke the prior blob URL before replacing it (and on teardown) so the
  // in-memory PDF object isn't leaked across re-selections.
  #setPreviewObjectUrl(url: string | null): void {
    if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl);
    this.previewObjectUrl = url;
  }

  get isDecided(): boolean {
    return this.status === 'approved' || this.status === 'denied';
  }

  @action addExpirationRow(): void {
    this.expirationEntries = [...this.expirationEntries, { name: '', date: null }];
  }

  get canRemoveRow(): boolean {
    return !this.requiresExpirationDate || this.expirationEntries.length > 1;
  }

  @action removeExpirationRow(index: number): void {
    if (!this.canRemoveRow) return;
    this.expirationEntries = this.expirationEntries.filter((_, i) => i !== index);
    void this.saveExpirationDatesTask.perform();
  }

  @action updateExpirationName(index: number, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.expirationEntries = this.expirationEntries.map((e, i) => (i === index ? { ...e, name: value } : e));
    void this.saveExpirationDatesTask.perform();
  }

  @action selectExpirationDate(index: number, date: Date): void {
    this.expirationEntries = this.expirationEntries.map((e, i) => (i === index ? { ...e, date } : e));
    this.closeCalendar();
    void this.saveExpirationDatesTask.perform();
  }

  // Build a fresh `{ label -> yyyy-MM-dd }` map from the current rows. Always a new
  // object — never mutates the (frozen) draft `fieldValues`.
  private buildInputFieldsData(): Record<string, string> {
    const data: Record<string, string> = {};
    for (const entry of this.expirationEntries) {
      if (entry.name && entry.date) {
        data[entry.name] = format(entry.date, 'yyyy-MM-dd');
      }
    }
    return data;
  }

  get hasIncompleteExpirationEntries(): boolean {
    return this.expirationEntries.some((e) => {
      const hasName = Boolean(e.name);
      const hasDate = e.date !== null;
      return hasName !== hasDate;
    });
  }

  // Persist edited expiration dates. Create mode PATCHes the draft via its model
  // apiAction; review mode PATCHes the visitor-document.
  private async persistFieldValues(): Promise<void> {
    if (this.isCreateMode) {
      if (!this.draft) return;
      await this.draft.updateFieldValues(this.buildInputFieldsData());
      return;
    }
    await this.patchInputFieldsData();
  }

  // Send a targeted PATCH with input_fields_data. Include `active` to
  // preserve its current value — the response pushes all attributes
  // back into the store.
  private async patchInputFieldsData(): Promise<void> {
    const doc = this.args.document;
    if (!doc) return;
    const data = this.buildInputFieldsData();
    const adapter = this.store.adapterFor('visitor-document');
    const url = adapter.buildURL('visitor-document', doc.id, null, 'v3');
    await adapter.ajax(url, 'PATCH', {
      data: {
        data: {
          type: 'visitor-documents',
          id: doc.id,
          attributes: {
            'input-fields-data': data,
            active: doc.active,
          },
        },
      },
    });
    doc.inputFieldsData = Object.freeze({ ...data });
  }

  // Eager auto-save: persists on every committed edit (no debounce) so edits land before a
  // human-paced invite submit — there is nothing deferred to "flush" at submit time, and the
  // async draft->document convert reads the freshly-PATCHed field_values. enqueueTask (NOT
  // restartable): EC cancellation can't abort the in-flight adapter.ajax PATCH, so a restart
  // would leave two full-map PATCHes racing and an older one could land last. enqueue runs saves
  // strictly FIFO — the next PATCH dispatches only after the prior resolves — so the last write to
  // the server is genuinely the newest, and `approveTask` routes its persist through this same
  // queue so an edit-then-approve can't overlap either. Skips incomplete rows. On a real failure,
  // surface it to the admin (auto-save is silent — they'd otherwise believe a compliance date
  // saved when it didn't) AND rethrow for telemetry.
  saveExpirationDatesTask = enqueueTask(async () => {
    // The invite convert reads field-values from the draft SERVER-side, so a submit must
    // not race an in-flight PATCH (an auto-approved compliance doc would carry stale dates).
    // Report this card busy through the same channel as uploads; clear once the FIFO drains.
    this.reportUploadInFlight(true, 'field-values-save');
    try {
      if (this.hasIncompleteExpirationEntries) return;
      this.saveErrorMessage = null;
      await this.persistFieldValues();
    } catch (e) {
      if (didCancel(e)) return;
      this.saveErrorMessage = 'Could not save expiration dates. Please try again.';
      throw e;
    } finally {
      if (!this.saveExpirationDatesTask.isQueued) this.reportUploadInFlight(false, 'field-values-save');
    }
  });

  // ----- create-mode upload/parse lifecycle (per-card) -----

  // Notify the spine when this card's in-flight state changes (only on change), so it can
  // disable Submit while any document is still uploading (the pre-confirm window) OR a
  // field-values PATCH is pending (the convert reads field-values server-side at save time).
  private reportUploadInFlight(isUploading: boolean, kind: DocumentBusyKind = 'upload'): void {
    const templateId = this.args.template?.id;
    if (!this.isCreateMode || !templateId) return;
    if (this.busyReported[kind] === isUploading) return;
    this.busyReported[kind] = isUploading;
    this.args.onUploadStateChange?.(templateId, isUploading, kind);
  }

  handleValidate = (result: ValidationResult): void => {
    if (!result.sizeValid) this.uploadErrorMessage = 'The file must be 10 MB or less.';
    else if (!result.typeValid) this.uploadErrorMessage = 'The file must be a PDF, JPG, or PNG.';
    else this.uploadErrorMessage = null;
  };

  handleFilePreview = (result: FileReader['result']): void => {
    this.previewDataUrl = typeof result === 'string' ? result : null;
  };

  // DirectUploader only invokes this for files that passed validation.
  handleFileSelected = (file: File | null): void => {
    if (!file) return;
    this.selectedFile = file;
    // PDFs render via a blob-URL iframe (Chrome blocks data: PDFs in frames);
    // images use the data-URL preview, so only mint an object URL for PDFs.
    this.#setPreviewObjectUrl(file.type === 'application/pdf' ? URL.createObjectURL(file) : null);
    // Set the phase synchronously so the dropzone doesn't flicker back before the task starts.
    this.uploadPhase = 'uploading';
    this.reportUploadInFlight(true);
    void this.uploadDraftTask.perform(file);
  };

  resetFile = (): void => {
    void this.uploadDraftTask.cancelAll();
    // Also drop any queued/in-flight expiration saves: a save that fails AFTER the
    // reset would otherwise set saveErrorMessage on the now-empty card.
    void this.saveExpirationDatesTask.cancelAll();
    this.selectedFile = null;
    this.previewDataUrl = null;
    this.#setPreviewObjectUrl(null);
    this.draft = null;
    this.expirationEntries = [];
    this.uploadErrorMessage = null;
    this.saveErrorMessage = null;
    this.uploadPhase = 'idle';
    this.reportUploadInFlight(false);
    this.reportUploadInFlight(false, 'field-values-save');
    if (this.args.template) this.args.onDraftChange?.(this.args.template.id, null);
    // Removing the file destroys the focused Remove button; return focus to the dropzone
    // input once it re-renders so keyboard users aren't dropped on <body>.
    runTask(this, () => {
      document.getElementById(this.uploadInputId)?.focus();
    });
  };

  // Per-row accessible names: with multiple coverages every row otherwise carries the
  // same label, so a screen-reader user can't tell which row they're on.
  expirationNameLabel = (index: number): string => `Expiration date name, row ${index + 1}`;

  expirationDateLabel = (entry: ExpirationEntry, index: number): string =>
    entry.name ? `Expiration date for ${entry.name}` : `Expiration date, row ${index + 1}`;

  // Poll the draft until the terminal `completed` status. Hosted on the card (not a
  // util) so cancellation stays task-linked: `uploadDraftTask` performs this as a
  // child, and a remove/replace or teardown cancels the loop at its next await. A
  // parse failure resolves as `completed` with an empty field-values map (callers
  // use `hasFieldValues`); a parse that exceeds `timeoutMs` REJECTS so a stuck parse
  // surfaces through `.last.error` instead of a silently non-terminal draft.
  pollUntilTerminalTask = task(async (draftDocument: DraftDocumentModel) => {
    const { intervalMs = 1000, timeoutMs = 30000 } = this.args.pollOptions ?? {};
    const startedAt = Date.now();
    let current = draftDocument;

    while (!current.isCompleted) {
      const remaining = timeoutMs - (Date.now() - startedAt);
      if (remaining <= 0) {
        throw new Error(`Draft document ${current.id} parsing timed out after ${timeoutMs}ms`);
      }
      await timeout(Math.min(intervalMs, remaining));
      // Bound the GET by the remaining budget so a hung poll can't skip the
      // next top-of-loop deadline check and hang the task forever.
      current = await pollDraftDocumentWithDeadline(
        this.store,
        current.id,
        Math.max(timeoutMs - (Date.now() - startedAt), 0),
      );
    }

    return current;
  });

  // Per-card upload/parse orchestration — restartable so re-selecting a file replaces
  // the run. Reports the draft id up immediately (the invite-create references it even
  // before parsing finishes), then polls to completion. A failed upload or a parse
  // timeout lands in `error` (recoverable via Remove) — never a perpetual spinner —
  // and retracts the reported id so a doc we couldn't confirm isn't submitted as if ready.
  uploadDraftTask = restartableTask(async (file: File) => {
    // `settled()` is blind to the raw-XHR S3 PUT and the EC `timeout()` poll inside this task;
    // hold a test waiter so submit/save can't race ahead of the final `onDraftChange` collect.
    const waiterToken = uploadWaiter.beginAsync();
    // EC cancellation interrupts THIS task at its awaits but can't reach into the raw-XHR
    // S3 PUT — abort it via the controller in `finally` (which runs on cancel too), so a
    // remove/replace/teardown doesn't leave the presigned upload streaming in the background.
    // Aborting after the PUT settled is a no-op (uploadFile guards re-entry).
    const uploadAbort = new AbortController();
    try {
      this.uploadErrorMessage = null;
      const template = this.args.template;
      if (!template) {
        this.uploadPhase = 'error';
        this.uploadErrorMessage = 'Unable to upload right now. Please try again.';
        return;
      }
      // Run-local so the catch below branches on THIS run's progress — `this.draft` could
      // hold a stale draft from a previous upload when a replacement file's upload fails.
      let created: DraftDocumentModel | null = null;
      try {
        this.uploadPhase = 'uploading';
        // request-link → upload → confirm are sequenced here (not in a util compose)
        // so each await is a cancellation point of THIS task: a remove/replace or
        // teardown mid-upload stops before the next step fires.
        const { draftDocumentId, uploadLink } = await requestUploadLink(this.store, {
          templateId: template.id,
          locationId: this.args.locationId,
          contentType: file.type,
          byteSize: file.size,
        });
        await uploadFile(uploadLink, file, { signal: uploadAbort.signal });
        created = await confirmDraftDocument(this.store, draftDocumentId, { templateId: template.id });
        this.draft = created;
        this.uploadPhase = 'parsing';
        // Draft now exists — leave the pre-confirm "uploading" window so Submit re-enables
        // (parsing does not block submit; the id is reported below for invite-create).
        this.reportUploadInFlight(false);
        this.args.onDraftChange?.(template.id, created.id);

        // Resolves only when the parse reaches `completed`; pollUntilTerminalTask REJECTS on
        // timeout (handled in catch). A parse that found nothing still resolves as completed
        // with an empty fieldValues map -> one blank row for manual entry (no `failed` status,
        // and none is needed here — empty and not-yet-entered look the same to the admin).
        const terminal = await this.pollUntilTerminalTask.perform(created);
        this.draft = terminal;
        this.seedExpirationEntriesFromMap(terminal.fieldValues ?? {});
        this.uploadPhase = 'completed';
        this.args.onDraftChange?.(template.id, terminal.id);
      } catch (e) {
        if (didCancel(e)) return;
        // Recoverable failure. If the draft was created, the parse/poll failed (e.g. timeout);
        // otherwise the upload itself failed. Retract the id either way — the admin Removes + retries.
        this.uploadPhase = 'error';
        this.uploadErrorMessage = created
          ? 'Parsing took too long. Remove the file and try again.'
          : 'Upload failed. Please try again.';
        this.reportUploadInFlight(false);
        this.args.onDraftChange?.(template.id, null);
      }
    } finally {
      uploadAbort.abort();
      uploadWaiter.endAsync(waiterToken);
    }
  });

  approveTask = dropTask(async () => {
    try {
      // Route the final persist through the FIFO save queue (not a separate patch) so it can't
      // overlap an in-flight edit save and land out of order; await it before approving.
      await this.saveExpirationDatesTask.perform();
      await this.documentLink?.approve();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  denyTask = dropTask(async () => {
    try {
      void this.saveExpirationDatesTask.cancelAll();
      await this.documentLink?.deny();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  resetTask = dropTask(async () => {
    try {
      await this.documentLink?.reset();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  <template>
    <UiCard
      @border={{not this.isCreateMode}}
      class="rounded-lg p-6 mb-4 overflow-visible
        {{if this.isCreateMode '!bg-carbon-5 !shadow-none border border-carbon-10'}}"
      data-test-document-review-card
      data-test-admin-prereg-document={{this.adminPreregTemplateId}}
      ...attributes
    >
      {{! Header: title + status badge. Create mode renders inside the "Required documents"
          <h3> section, so the card title is an <h4> to keep heading levels nested. }}
      <div class="flex items-center justify-between mb-2">
        {{#if this.isCreateMode}}
          <h4 class="text-base font-medium m-0" data-test-document-title>{{this.title}}</h4>
        {{else}}
          <h3 class="text-lg font-semibold m-0" data-test-document-title>{{this.title}}</h3>
        {{/if}}
        {{#if this.showStatusBadge}}
          <DocumentStatusBadge @status={{this.status}} />
        {{/if}}
      </div>

      {{#if this.isCreateMode}}
        {{! ===== CREATE MODE: dropzone -> upload -> parse -> rows ===== }}

        {{! Persistent live region: announces both the in-progress phase and completion
            ("editable rows appeared") to assistive tech. The visible parsing block is
            aria-hidden; errors announce via role="alert". Present-then-filled (not
            inserted-with-content) for reliable SR delivery. }}
        <span class="sr-only" role="status" aria-live="polite" data-test-draft-status-announcement>
          {{if this.createInProgress this.parsingLabel}}{{if
            this.createCompleted
            "Document ready. Review the expiration dates below."
          }}
        </span>

        {{! In-memory preview of the selected file (hoisted — one copy shared by every state):
            the rendered content (image, or PDF first page via a blob-URL iframe) is clipped to a
            166px Carbon/10 frame showing the document top — matching Figma 1170-1955. }}
        {{#if this.selectedFile}}
          <div
            class="relative rounded-md overflow-hidden mb-4 bg-carbon-10 border-2 border-carbon-10"
            data-test-draft-preview
          >
            {{#if this.hasImagePreview}}
              <img
                src={{this.previewDataUrl}}
                alt="Preview of uploaded document"
                class="w-full h-[166px] object-cover object-top"
                data-private
              />
            {{else if this.createPreviewPdfUrl}}
              <iframe
                src={{this.createPreviewPdfUrl}}
                title="Document preview"
                class="w-full h-[166px] border-0 pointer-events-none"
                data-private
              ></iframe>
            {{else}}
              {{! Fallback (URL not yet ready / unexpected type): name the file rather than show nothing. }}
              <div class="flex items-center gap-3 p-4">
                <span class="flex-none">{{svgJar "document" height="24"}}</span>
                <span class="flex-1 font-medium text-carbon-70 overflow-hidden" data-test-draft-filename data-private>
                  {{this.selectedFile.name}}
                </span>
              </div>
            {{/if}}
          </div>
        {{/if}}

        {{#if (or this.createCompleted this.createErrored)}}
          {{! Terminal state (parsed OR failed/timed-out): always offer a way out }}
          <div class="flex mb-4" data-test-draft-actions>
            <button
              type="button"
              class="text-sm text-carbon-50 hover:text-red-50 focus-visible:underline focus-visible:text-red-50"
              data-test-remove-draft
              aria-label="Remove uploaded document"
              {{on "click" this.resetFile}}
            >
              Remove
            </button>
          </div>
        {{else if this.createInProgress}}
          <p
            class="flex items-center gap-2 text-sm text-carbon-50 mt-0 mb-4"
            data-test-admin-prereg-document-parsing
            aria-hidden="true"
          >
            <img src="/assets/images/progress-small-gray.svg" alt="" aria-hidden="true" class="w-4 h-4" />
            {{this.parsingLabel}}
          </p>
        {{else}}
          {{! Dropzone — DirectUploader handles drag/drop, type + size validation, and preview }}
          <DirectUploader
            @accept={{this.acceptedFileTypes}}
            @validType={{this.acceptedMimeRegex}}
            @validSize={{this.maxFileSizeBytes}}
            @autoUpload={{false}}
            @enqueue={{false}}
            @showResetButton={{false}}
            @showErrorState={{false}}
            @onValidate={{this.handleValidate}}
            @onFilePreview={{this.handleFilePreview}}
            @onFileSelected={{this.handleFileSelected}}
            @inputId={{this.uploadInputId}}
            @inputAriaLabel={{this.uploadInputAriaLabel}}
            {{! White dropzone card on the gray section (Figma 978-5376: Base/White fill,
                Carbon/10 solid border). Without bg-white the transparent dropzone shows the
                gray section through it. }}
            class="h-40 bg-white !border-solid border-carbon-10"
            data-test-draft-dropzone
          >
            <:label>
              {{! Tied to the input via for/id so the dropzone input has an accessible name
                  and clicking the text opens the file picker. }}
              <label for={{this.uploadInputId}} class="cursor-pointer">
                <span class="btn--link !text-gem-50 text-sm">Upload a document</span>
                <span class="!text-sm">or drag and drop here</span>
              </label>
            </:label>
          </DirectUploader>
          <p class="text-sm text-carbon-40 mt-2 mb-0" data-test-accepted-formats>
            Accepted formats: .pdf, .jpg, .png.
          </p>
        {{/if}}

        {{#if this.uploadErrorMessage}}
          <p class="text-xs text-red-50 mt-1 mb-2" data-test-draft-error role="alert" aria-live="assertive">
            {{this.uploadErrorMessage}}
          </p>
        {{/if}}

      {{else if @isUploaded}}
        {{! ===== REVIEW MODE: uploaded-document preview ===== }}
        {{#if this.uploadDate}}
          <p class="text-sm text-carbon-50 mt-0 mb-4" data-test-upload-date>
            Uploaded
            {{this.uploadDate}}
          </p>
        {{/if}}

        {{#if this.isDiscarded}}
          <div class="rounded-md border border-carbon-10 overflow-hidden mb-4 bg-carbon-5" data-test-document-discarded>
            <div class="w-full min-h-80 flex items-center justify-center">
              <span class="text-carbon-70 text-sm font-normal italic">Document discarded</span>
            </div>
          </div>
        {{else if this.externalAttachmentUrl}}
          <div
            class="group relative rounded-md border border-carbon-20 overflow-hidden mb-4 bg-carbon-10"
            data-test-document-preview
          >
            {{#if this.isPdf}}
              <iframe
                src={{this.pdfViewerUrl}}
                title={{this.documentFileName}}
                class="w-full min-h-80 border-0"
                data-private
              />
              {{! Transparent div captures hover over iframe so group-hover triggers }}
              <div class="absolute inset-0"></div>
            {{else}}
              <img
                src={{this.externalAttachmentUrl}}
                alt={{this.title}}
                class="w-full object-contain max-h-80"
                data-private
                {{on "error" this.handleImageError}}
              />
            {{/if}}
            <div class={{this.mediaOverlayClass}}>
              <a
                href={{this.externalAttachmentUrl}}
                download
                target="_blank"
                rel="noopener noreferrer"
                class="btn btn--small btn--outline flex items-center gap-2 !bg-carbon-5 !rounded-md pointer-events-auto"
                data-test-download-button
              >
                {{svgJar "download" width="14" height="14" class="shrink-0 -mt-1"}}
                <span>Download</span>
              </a>
              <button
                type="button"
                class="btn btn--small btn--outline flex items-center gap-2 !bg-carbon-5 !rounded-md pointer-events-auto"
                {{on "click" this.openDocument}}
                data-test-open-button
              >
                {{svgJar "expand-corners" width="12" height="12" class="shrink-0 -mt-1"}}
                <span>Open</span>
              </button>
            </div>
          </div>

          {{#if this.showPreviewModal}}
            <DocumentPreviewModal
              @documents={{this.previewDocuments}}
              @initialIndex={{0}}
              @onClose={{this.closePreviewModal}}
            />
          {{/if}}
        {{/if}}
      {{else}}
        {{! Not uploaded placeholder }}
        <div class="py-8 text-center text-carbon-50" data-test-not-uploaded-placeholder>
          Document not yet uploaded
        </div>
      {{/if}}

      {{! ===== Shared editable expiration rows (review-uploaded OR create-completed) ===== }}
      {{#if this.showExpirationRows}}
        <div
          class="mt-4 {{if this.isCreateMode 'bg-white rounded-lg border border-carbon-10 p-4'}}"
          data-test-expiration-fields
          {{this.captureExpirationFields}}
          {{this.seedFromControlledArgs @status @fieldValues}}
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-carbon-50 leading-none">Expiration date(s)</span>
            <button
              class="text-sm text-gem-50 hover:underline"
              type="button"
              data-test-add-expiration
              {{on "click" this.addExpirationRow}}
            >
              Add
            </button>
          </div>

          {{#if this.parseReturnedNoDates}}
            <p class="text-xs text-carbon-50 mb-2" data-test-no-dates-detected>
              No expiration dates were detected — add them below if this document has any.
            </p>
          {{/if}}

          {{! key by index: edits replace the (immutable) entry objects, and identity keying
              would tear down + recreate every row's DOM on each committed edit — dropping
              keyboard focus mid-entry (name -> date tabbing landed on <body>). }}
          {{#each this.expirationEntries key="@index" as |entry index|}}
            <div class="flex items-center gap-2 mb-3" data-test-expiration-row>
              <input
                type="text"
                value={{entry.name}}
                placeholder="Name"
                aria-label={{this.expirationNameLabel index}}
                class="flex-1 text-sm border rounded px-3 h-10 !mb-0
                  {{if (and (not entry.name) entry.date) 'border-red-50' 'border-carbon-20'}}"
                required
                data-test-expiration-name
                {{on "change" (fn this.updateExpirationName index)}}
              />
              <div class="relative">
                <button
                  type="button"
                  class="flex items-center justify-start gap-1.5 w-[130px] px-3 text-sm border rounded bg-white cursor-pointer transition-colors duration-150 hover:border-carbon-40 h-10
                    {{if (eq this.openCalendar index) 'border-gem-50' 'border-carbon-20'}}"
                  aria-label={{this.expirationDateLabel entry index}}
                  data-test-expiration-date
                  {{on "click" (fn this.toggleCalendar index)}}
                >
                  <span class="flex-none text-carbon-40">{{svgJar "calendar-16" width="16" height="16"}}</span>
                  <span class="{{unless entry.date 'text-carbon-40'}}">{{this.formatDate entry.date}}</span>
                </button>
                {{! template-lint-disable use-ui-card }}
                {{#if (eq this.openCalendar index)}}
                  <div
                    class="absolute right-0 z-50 mt-1 p-3 bg-white border border-carbon-20 rounded-lg shadow-lg"
                    data-test-expiration-calendar
                  >
                    <EnvoyOneUpCalendar
                      @selectedDate={{entry.date}}
                      @onSelectDate={{fn this.selectExpirationDate index}}
                    />
                  </div>
                {{/if}}
              </div>
              {{#if this.canRemoveRow}}
                <button
                  class="flex-none w-8 h-10 flex items-center justify-center text-carbon-50 hover:text-red-50"
                  type="button"
                  data-test-remove-expiration
                  {{on "click" (fn this.removeExpirationRow index)}}
                >
                  {{svgJar "cross-black" width="16" height="16" class="[&_path]:fill-current"}}
                </button>
              {{else}}
                <div class="flex-none w-8 h-10"></div>
              {{/if}}
            </div>
          {{/each}}
          {{#if this.showIncompleteRowError}}
            <p class="text-xs text-red-50 mt-1" data-test-expiration-validation role="alert" aria-live="assertive">
              Both name and date are required for each entry.
            </p>
          {{/if}}
          {{#if this.saveErrorMessage}}
            <p class="text-xs text-red-50 mt-1" data-test-expiration-save-error role="alert" aria-live="assertive">
              {{this.saveErrorMessage}}
            </p>
          {{/if}}
        </div>
      {{/if}}

      {{! ===== Review-mode actions (approve / deny / undo) — never on the create form ===== }}
      {{#if (and @isUploaded (not this.isCreateMode))}}
        {{#if this.isPending}}
          {{#if (and this.requiresExpirationDate (not this.hasAnyCompleteDate))}}
            <p class="text-xs text-red-50 mt-2 mb-0" data-test-expiration-required role="alert" aria-live="assertive">
              At least one expiration date is required before approving
            </p>
          {{/if}}
          <div class="flex gap-4 mt-4" data-test-document-actions>
            <button
              class="flex-1 flex items-center justify-center gap-2 px-4 py-[11px] bg-white border border-carbon-20 rounded-md text-base font-medium text-carbon-70 hover:bg-cilantro-10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-test-approve-button
              disabled={{this.approveDisabled}}
              type="button"
              {{on "click" (perform this.approveTask)}}
            >
              {{svgJar "checkmark-green" width="16" height="16"}}
              Approve
            </button>
            <button
              class="flex-1 flex items-center justify-center gap-2 px-4 py-[11px] bg-white border border-carbon-20 rounded-md text-base font-medium text-carbon-70 hover:bg-red-10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-test-deny-button
              disabled={{this.denyTask.isRunning}}
              type="button"
              {{on "click" (perform this.denyTask)}}
            >
              {{svgJar "cross-small-red" width="16" height="16"}}
              Deny
            </button>
          </div>
        {{else if (and this.isDecided @showUndo)}}
          <div class="flex mt-4" data-test-document-actions>
            <button
              class="px-4 py-[11px] bg-white border border-carbon-20 rounded-md text-sm font-medium text-carbon-70 hover:bg-carbon-5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-test-undo-button
              disabled={{this.resetTask.isRunning}}
              type="button"
              {{on "click" (perform this.resetTask)}}
            >
              Undo
            </button>
          </div>
        {{/if}}
      {{/if}}
    </UiCard>
  </template>
}
